import SimpleBar from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { setActiveItem } from "units/dashboard/store/menuSlice";
import { useEffect, useRef, useState } from "react";
import { useClientSidebar as useSidebar } from "units/client/context/ClientSidebarProvider";
import {
  MessageSquare,
  Plus,
  Wallet,
  CreditCard,
  UserRound,
  KeyRound,
  PanelRightClose,
  PanelRightOpen,
  X,
  LogOut,
  Globe,
  Sun,
  Moon,
  Languages,
  Trash2,
  Settings,
  Sidebar
} from "lucide-react";
import "../ClientChatBot/styles/clientChat.responsive.css";
import { logout } from "units/auth/store/authSlice";
import ConfirmDeleteModal from "../ClientChatBot/components/ConfirmDeleteModal";
import { useLanguage } from "units/dashboard/context/LanguageProvider";
import SettingsModal from "../ClientChatBot/components/SettingsModal";
import { useTheme } from "units/dashboard/context/ThemeProvider";
import config from "../../../config.js";
import { useTranslation } from "react-i18next";
import { groupConversationsByDate, GROUP_KEYS } from "../utils/groupConversationsByDate";
const LONG_PRESS_DURATION = 550; // ms

const ClientSidebar = ({
  username,
  name,
  conversations = [],
  onNewChat,
  onConversationClick,
  onDeleteConversation,
  activeConversationId,
  isCustomerPaymentActive
}) => {
  const { theme, toggleTheme } = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isSidebarOpen, toggleSidebar, closeSidebar, isMobile } =
    useSidebar();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [active, setActive] = useState(null);
  const { t } = useTranslation(["client", "common"]);

  // ------- Delete flow state -------
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, title }
  const [isDeleting, setIsDeleting] = useState(false);

  // ------- Long-press refs (per-item timer via closures) -------
  const pressTimerRef = useRef(null);
  const longPressTriggeredRef = useRef(false);
  const BASE_URL = config.cdnUrl;
  const logo = JSON.parse(localStorage.getItem("logo") || "null");
  const groupLabelKeys = {
    [GROUP_KEYS.TODAY]: "sidebar.groupToday",
    [GROUP_KEYS.YESTERDAY]: "sidebar.groupYesterday",
    [GROUP_KEYS.LAST_WEEK]: "sidebar.groupLastWeek",
    [GROUP_KEYS.OLDER]: "sidebar.groupOlder",
  };
  const groupedConversations = groupConversationsByDate(conversations);
  useEffect(() => {
    const path = location.pathname;

    if (path.includes(`/bot/chatbox/${username}`)) {
      setActive("chatbox");
    } else if (path.includes(`/bot/wallet/${username}`)) {
      setActive("wallet");
    } else if (path.includes(`/bot/client-transaction-list/${username}`)) {
      setActive("client-transaction-list");
    } else if (path.includes(`/bot/edit/${username}`)) {
      setActive("edit");
    } else if (path.includes(`/bot/client-change-password/${username}`)) {
      setActive("client-change-password");
    } else {
      setActive(null);
    }
  }, [location.pathname, username]);

  const goAndMaybeClose = (fn) => {
    fn();
    if (isMobile) closeSidebar();
  };

  const handleNewChat = () =>
    goAndMaybeClose(() => {
      dispatch(setActiveItem("chatbox"));
      onNewChat?.();
    });

  const handleConversationClick = (conversation) => {
    if (!conversation) return;

    goAndMaybeClose(() => {
      dispatch(setActiveItem("chatbox"));
      onConversationClick?.(conversation);
    });
  };

  const handleNavigate = (page, activeName) =>
    goAndMaybeClose(() => {
      setActive(activeName);
      navigate(`/bot/${page}/${username}`);
    });

  const handleLogout = (username) => {
    dispatch(logout());
    localStorage.removeItem("logo");
    localStorage.removeItem("phoneNumber");
    localStorage.removeItem("countryCode");
    localStorage.removeItem("user");
    localStorage.removeItem("botInfo");
    navigate(`/${username}`);
  };

  const getConversationTitle = (conversation) =>
    conversation?.title ||
    conversation?.name ||
    conversation?.conversationTitle ||
    conversation?.roomName ||
    t("sidebar.newChat");

  const getConversationId = (conversation, index) =>
    conversation?.id || conversation?.chatroomId || `conversation-${index}`;

  const showLabels = isMobile || isSidebarOpen;

  /* -------------------------------------------------------------------------- */
  /*                              Delete Flow                                    */
  /* -------------------------------------------------------------------------- */

  const openDeleteModal = (conversation, index) => {
    setDeleteTarget({
      id: getConversationId(conversation, index),
      title: getConversationTitle(conversation),
    });
  };

  const closeDeleteModal = () => {
    if (isDeleting) return; // در حال حذف، اجازه‌ی بستن نده
    setDeleteTarget(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    setIsDeleting(true);

    try {
      await onDeleteConversation?.(deleteTarget.id);
      setDeleteTarget(null);
    } catch (error) {
      // خطا از سمت والد (toast) هندل میشه؛ مودال باز می‌مونه تا کاربر دوباره تلاش کنه
    } finally {
      setIsDeleting(false);
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                              Long Press (mobile)                            */
  /* -------------------------------------------------------------------------- */

  const startLongPress = (conversation, index) => {
    longPressTriggeredRef.current = false;

    pressTimerRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true;
      openDeleteModal(conversation, index);

      if (navigator.vibrate) navigator.vibrate(15); // فیدبک لمسی خفیف
    }, LONG_PRESS_DURATION);
  };

  const cancelLongPress = () => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
  };

  const handleConversationItemClick = (conversation) => {
    if (longPressTriggeredRef.current) {
      longPressTriggeredRef.current = false;
      return; // long-press بود، navigate نکن
    }
    handleConversationClick(conversation);
  };

  const menuItemBase = `
    w-full flex items-center rounded-xl
    transition-all duration-300 ease-out
    relative overflow-hidden
    group
  `;

  const menuItemInactive = `
    text-gray-600 dark:!text-zinc-400
    hover:text-teal-700 dark:!hover:text-teal-300
    hover:bg-gradient-to-l hover:from-teal-500/10 hover:to-cyan-500/5
    hover:shadow-sm hover:shadow-teal-500/10
    hover:translate-x-[-2px]
  `;

  const menuItemActive = `
    bg-gradient-to-l from-teal-500/20 via-teal-500/10 to-cyan-500/5
    text-teal-700 dark:!text-teal-300
    shadow-sm shadow-teal-500/20
    ring-1 ring-teal-400/30
    before:absolute before:right-0 before:top-1/2 before:-translate-y-1/2
    before:w-1 before:h-6 before:rounded-full
    before:bg-gradient-to-b before:from-teal-400 before:to-cyan-500
    before:shadow-[0_0_8px_rgba(20,184,166,0.6)]
  `;

  return (
    <>
      <div
        className={`client-sidebar-backdrop ${isMobile && isSidebarOpen ? "client-sidebar-backdrop--visible" : ""
          }`}
        onClick={closeSidebar}
      />

      <aside
        className={`
          client-sidebar
          bg-white dark:!bg-zinc-900
          border-l border-teal-100/60 dark:!border-teal-900/30
          shadow-lg shadow-teal-500/5
          ${!isMobile && isSidebarOpen ? "client-sidebar--open" : ""}
          ${isMobile && isSidebarOpen ? "client-sidebar--mobile-open" : ""}
        `}
      >
        <div className="flex flex-col h-full">
          {/* ============= هدر (ثابت) ============= */}
          <div
            className={`shrink-0 h-[64px] flex items-center border-b border-teal-100/60 dark:!border-teal-900/30
    bg-gradient-to-l from-teal-50/50 via-white to-cyan-50/30
    dark:!from-teal-950/30 dark:!via-zinc-900 dark:!to-cyan-950/20
    ${showLabels ? "justify-between px-4" : "justify-center"}
  `}
          >
            {showLabels && (
              <div className="flex items-center gap-3 min-w-0 flex-1 ml-2">
                {/* ===== عکس ===== */}
                <img
                  src={logo ? `${BASE_URL}${logo}` : "/defaultLogo.png"}
                  alt={t("sidebar.assistant")}
                  className="shrink-0 w-10 h-10 rounded-full object-cover ring-2 ring-teal-400/30 shadow-sm shadow-teal-500/20"
                />

                {/* ===== متن‌ها ===== */}
                <div className="min-w-0 flex-1 text-right">
                  <p className="text-sm font-semibold text-gray-800 dark:!text-white truncate">
                    {username || t("sidebar.assistant")}
                  </p>
                  <span className="text-[11px] text-teal-600/70 dark:!text-teal-400/70">
                    {t("sidebar.assistantTagline")}
                  </span>
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={toggleSidebar}
              className="
      w-9 h-9 shrink-0 flex items-center justify-center rounded-lg
      text-gray-500 dark:!text-gray-300
      hover:bg-gradient-to-br hover:from-teal-500/15 hover:to-cyan-500/10
      hover:text-teal-700 dark:!hover:text-teal-300
      hover:ring-1 hover:ring-teal-400/30
      transition-all duration-200
      active:scale-95
    "
              title={isSidebarOpen ? t("sidebar.closeSidebar") : t("sidebar.openSidebar")}
            >
              {isMobile ? (
                <X className="w-5 h-5" />
              ) : isSidebarOpen ? (
                <PanelRightClose className="w-5 h-5" />
              ) : (
                <PanelRightOpen className="w-5 h-5" />
              )}
            </button>
          </div>

          {/* ============= منو (ثابت) ============= */}
          <div className="shrink-0 p-3 space-y-1">
            <button
              type="button"
              onClick={handleNewChat}
              className={`
                w-full flex items-center rounded-xl
                transition-all duration-300 ease-out
                bg-gradient-to-l from-teal-500 via-teal-600 to-cyan-700
                text-white font-medium
                shadow-md shadow-teal-500/30
                ring-1 ring-teal-300/40
                hover:shadow-lg hover:shadow-teal-500/50
                hover:-translate-y-0.5
                hover:ring-teal-200/60
                active:scale-[0.98] active:translate-y-0
                ${showLabels ? "gap-3 px-3 py-2.5 justify-start" : "justify-center px-0 py-2.5"}
              `}
              title={t("sidebar.newChat")}
            >
              <Plus className="w-5 h-5 shrink-0" />
              {showLabels && <span className="text-sm">{t("sidebar.newChat")}</span>}
            </button>
            {isCustomerPaymentActive &&
              <button
                type="button"
                onClick={() => handleNavigate("wallet", "wallet")}
                className={`
                ${menuItemBase}
                ${showLabels ? "gap-3 px-3 py-2.5 justify-start" : "justify-center px-0 py-2.5"}
                ${active === "wallet" ? menuItemActive : menuItemInactive}
              `}
                title={t("sidebar.wallet")}
              >
                <Wallet className="w-5 h-5 shrink-0 relative z-10" />
                {showLabels && (
                  <span className="text-sm font-medium relative z-10">
                    {t("sidebar.wallet")}
                  </span>
                )}
              </button>}

            {isCustomerPaymentActive &&
              <button
                type="button"
                onClick={() =>
                  handleNavigate(
                    "client-transaction-list",
                    "client-transaction-list"
                  )
                }
                className={`
                ${menuItemBase}
                ${showLabels ? "gap-3 px-3 py-2.5 justify-start" : "justify-center px-0 py-2.5"}
                ${active === "client-transaction-list" ? menuItemActive : menuItemInactive}
              `}
                title={t("sidebar.transactions")}
              >
                <CreditCard className="w-5 h-5 shrink-0 relative z-10" />
                {showLabels && (
                  <span className="text-sm font-medium relative z-10">
                    {t("sidebar.transactions")}
                  </span>
                )}
              </button>
            }


            <button
              type="button"
              onClick={() => handleNavigate("edit", "edit")}
              className={`
                ${menuItemBase}
                ${showLabels ? "gap-3 px-3 py-2.5 justify-start" : "justify-center px-0 py-2.5"}
                ${active === "edit" ? menuItemActive : menuItemInactive}
              `}
              title={t("sidebar.editProfile")}
            >
              <UserRound className="w-5 h-5 shrink-0 relative z-10" />
              {showLabels && (
                <span className="text-sm font-medium relative z-10">
                  {t("sidebar.editProfile")}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() =>
                handleNavigate(
                  "client-change-password",
                  "client-change-password"
                )
              }
              className={`
                ${menuItemBase}
                ${showLabels ? "gap-3 px-3 py-2.5 justify-start" : "justify-center px-0 py-2.5"}
                ${active === "client-change-password" ? menuItemActive : menuItemInactive}
              `}
              title={t("sidebar.changePassword")}
            >
              <KeyRound className="w-5 h-5 shrink-0 relative z-10" />
              {showLabels && (
                <span className="text-sm font-medium relative z-10">
                  {t("sidebar.changePassword")}
                </span>
              )}
            </button>
          </div>

          {/* ============= گفتگوهای قبلی ============= */}
          {showLabels && (
            <div className="flex-1 min-h-0 flex flex-col px-3 pb-2">
              {/* عنوان واضح‌تر و بخش‌بندی‌شده */}
              <div className="shrink-0 flex items-center gap-2 px-2 pt-1 pb-2.5">
                <span className="text-[13px] font-bold tracking-wide text-gray-700 dark:!text-gray-200">
                  {t("sidebar.previousChats")}
                </span>

                {conversations?.length > 0 && (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-teal-500/15 text-teal-700 dark:!text-teal-300 min-w-[18px] text-center">
                    {conversations.length}
                  </span>
                )}

                <div className="flex-1 h-px bg-gradient-to-l from-teal-400/30 to-transparent" />
              </div>

              <SimpleBar className="flex-1 min-h-0">
                <div className="space-y-3 pr-1">
                  {groupedConversations.length > 0 ? (
                    groupedConversations.map((group) => (
                      <div key={group.key} className="space-y-1">
                        {/* عنوان گروه زمانی */}
                        <div className="px-3 pt-1 pb-1">
                          <span className="text-[11px] font-bold text-gray-600 dark:!text-zinc-300 tracking-wide">
                            {t(groupLabelKeys[group.key])}
                          </span>
                        </div>

                        {group.items.map((conversation, index) => {
                          const conversationId = getConversationId(conversation, index);
                          const title = getConversationTitle(conversation);
                          const isActiveConversation =
                            !!activeConversationId && conversationId === activeConversationId;

                          return (
                            <div
                              key={conversationId}
                              className={`
                  conversation-item
                  relative flex items-center rounded-lg
                  transition-all duration-200
                  ${isActiveConversation
                                  ? `
                          bg-gradient-to-l from-teal-500/20 via-teal-500/10 to-cyan-500/5
                          ring-1 ring-teal-400/30
                          shadow-sm shadow-teal-500/10
                        `
                                  : "hover:bg-gradient-to-l hover:from-teal-500/10 hover:to-transparent"
                                }
                `}
                            >
                              {isActiveConversation && (
                                <span className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-full bg-gradient-to-b from-teal-400 to-cyan-500 shadow-[0_0_8px_rgba(20,184,166,0.6)]" />
                              )}

                              <button
                                type="button"
                                onClick={() => handleConversationItemClick(conversation)}
                                onTouchStart={() => startLongPress(conversation, index)}
                                onTouchEnd={cancelLongPress}
                                onTouchMove={cancelLongPress}
                                onContextMenu={(e) => e.preventDefault()}
                                className={`
                    conversation-item-touchable
                    flex-1 min-w-0 flex items-center gap-2 px-3 py-2 rounded-lg text-right
                    text-[13px]
                    ${isActiveConversation
                                    ? "text-teal-700 dark:!text-teal-300"
                                    : "text-gray-600 dark:!text-gray-400 hover:text-teal-700 dark:!hover:text-teal-300"
                                  }
                    ${!isMobile ? "pl-8" : ""}
                  `}
                                title={title}
                              >
                                <MessageSquare
                                  className={`w-4 h-4 shrink-0 transition-colors ${isActiveConversation
                                    ? "text-teal-600 dark:!text-teal-400"
                                    : "text-gray-400 dark:!text-zinc-500"
                                    }`}
                                />
                                <span className="truncate">{title}</span>
                              </button>

                              {!isMobile && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openDeleteModal(conversation, index);
                                  }}
                                  title={t("sidebar.deleteConversation")}
                                  className="
                      conversation-trash-btn
                      absolute end-1.5 top-1/2 -translate-y-1/2
                      w-6 h-6 flex items-center justify-center rounded-md
                      text-gray-400 dark:!text-zinc-500
                      hover:text-rose-600 dark:!hover:text-rose-400
                      hover:bg-rose-500/10
                      transition-colors
                    "
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))
                  ) : (
                    <div className="px-3 py-4 text-center text-xs text-gray-400 dark:!text-zinc-500">
                      {t("sidebar.noConversations")}
                    </div>
                  )}
                </div>
              </SimpleBar>
            </div>
          )}

          {/* ============= فوتر (ثابت) ============= */}
          <div
            className=" relative z-20
    shrink-0 mt-auto p-3
    border-t border-teal-100/60 dark:!border-teal-900/30
    bg-gradient-to-l from-teal-50/40 via-transparent to-cyan-50/20
    dark:!from-teal-950/20 dark:!via-transparent dark:!to-cyan-950/10
    space-y-1"
          >
            <button
              type="button"
              onClick={() => setIsSettingsOpen(true)}
              className={`
                relative z-30
    w-full flex items-center rounded-xl
    transition-all duration-300 ease-out
    text-gray-600 dark:!text-zinc-400
    hover:text-teal-700 dark:!hover:text-teal-300
    hover:bg-gradient-to-l hover:from-teal-500/10 hover:to-cyan-500/5
    hover:ring-1 hover:ring-teal-400/30
    hover:shadow-sm hover:shadow-teal-500/10
    active:scale-[0.98]
    ${showLabels ? "gap-3 px-3 py-2.5 justify-start" : "justify-center px-0 py-2.5"}
  `}
              title={t("settingsModal.title")}
            >
              <Settings className="w-5 h-5 shrink-0" />
              {showLabels && (
                <span className="text-sm font-medium">
                  {t("settingsModal.title")}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() => handleLogout(username)}
              className={`
                relative z-30
                w-full flex items-center rounded-xl
                transition-all duration-300 ease-out
                text-rose-600 dark:!text-rose-400
                bg-gradient-to-l from-rose-500/5 to-transparent
                hover:from-rose-500/15 hover:to-rose-500/5
                hover:ring-1 hover:ring-rose-400/30
                hover:shadow-sm hover:shadow-rose-500/20
                hover:-translate-y-0.5
                active:scale-[0.98] active:translate-y-0
                ${showLabels ? "gap-3 px-3 py-2.5 justify-start" : "justify-center px-0 py-2.5"}
              `}
              title={t("sidebar.logout")}
            >
              <LogOut className="w-5 h-5 shrink-0" />
              {showLabels && (
                <span className="text-sm font-medium">
                  {t("sidebar.logout")}
                </span>
              )}
            </button>
          </div>
        </div>
      </aside>

      <ConfirmDeleteModal
        isOpen={!!deleteTarget}
        title={deleteTarget?.title}
        isDeleting={isDeleting}
        onCancel={closeDeleteModal}
        onConfirm={handleConfirmDelete}
      />
      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />
    </>
  );
};

export default ClientSidebar;